import React from 'react';

const field = { display: 'block', width: '100%', boxSizing: 'border-box', padding: 8,
  border: '1px solid #ddd', borderRadius: 6, margin: '4px 0 10px' };
const button = { padding: '5px 10px', border: '1px solid #ddd', borderRadius: 6,
  background: '#fff', cursor: 'pointer' };

export function audienceSlug(label, existing = []) {
  let base = label.normalize('NFKD').replace(/[\u0300-\u036f]/g, '').toLowerCase()
    .replace(/[^a-z0-9_-]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 32);
  if (!/^[a-z]/.test(base)) base = `audience-${base}`.slice(0, 32);
  if (base.length < 2) base += '-audience';
  let id = base;
  for (let n = 2; existing.includes(id); n += 1) id = `${base.slice(0, 28)}-${n}`;
  return id;
}

export function AudienceVocabulary({ audiences, docs, onSave, disabled = false }) {
  const [draft, setDraft] = React.useState(null);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState('');
  const counts = Object.fromEntries(audiences.map((a) =>
    [a.id, docs.filter((d) => (d.audiences || []).includes(a.id)).length]));
  const save = async (next) => {
    setSaving(true); setError('');
    try { await onSave(next); setDraft(null); }
    catch (e) { setError(e.message); }
    finally { setSaving(false); }
  };
  const submit = (e) => {
    e.preventDefault();
    const next = { ...draft, label: draft.label.trim() };
    if (!next.label) return;
    if (!next.id) next.id = audienceSlug(next.label, audiences.map((a) => a.id));
    save(draft.id ? audiences.map((a) => a.id === draft.id ? next : a) : [...audiences, next]);
  };
  return (
    <section aria-label="Audience vocabulary" style={{ border: '1px solid #e5e7eb', borderRadius: 10, padding: 16, marginBottom: 18 }}>
      <h3 style={{ margin: '0 0 6px', fontSize: 16 }}>Audiences</h3>
      <p style={{ fontSize: 13, color: '#666', margin: '0 0 12px' }}>
        Define who your sources serve. Untagged sources are available to everyone. Audiences are shared by all sites using this corpus.
      </p>
      {audiences.map((a) => (
        <div key={a.id} style={{ padding: '10px 0', borderTop: '1px solid #eee', display: 'flex', gap: 12, alignItems: 'start' }}>
          <div style={{ flex: 1 }}>
            <strong>{a.label}</strong> <span style={{ fontSize: 12, color: '#777' }}>{counts[a.id]} tagged documents</span>
            {a.description && <div style={{ fontSize: 13, color: '#555' }}>{a.description}</div>}
            {(a.starter_questions || []).map((q, i) => <div key={i} style={{ fontSize: 12, marginTop: 3 }}>{q}</div>)}
          </div>
          <button type="button" style={button} disabled={saving || disabled} aria-label={`Edit ${a.label}`}
            onClick={() => { setDraft({ ...a, starter_questions: [...(a.starter_questions || [])] }); setError(''); }}>Edit</button>
          <div>
            <button type="button" style={button} disabled={saving || disabled || counts[a.id] > 0 || audiences.length === 1}
              aria-label={`Delete ${a.label}`} onClick={() => save(audiences.filter((x) => x.id !== a.id))}>Delete</button>
            {(counts[a.id] > 0 || audiences.length === 1) && <div style={{ fontSize: 11, color: '#777', maxWidth: 180, marginTop: 4 }}>
              {counts[a.id] > 0 ? `Filter by ${a.label} and bulk-remove its tags first.` : 'Keep at least one audience; turn off the gate in Widget to stop asking.'}
            </div>}
          </div>
        </div>
      ))}
      {!draft && <button type="button" style={button} disabled={saving || disabled || audiences.length >= 8}
        onClick={() => { setDraft({ id: '', label: '', description: '', starter_questions: [] }); setError(''); }}>Add audience</button>}
      {draft && <form onSubmit={submit} style={{ maxWidth: 560, marginTop: 12 }}>
        <label>Label<input required maxLength={40} style={field} value={draft.label}
          onChange={(e) => setDraft({ ...draft, label: e.target.value })} /></label>
        <label>Description<input maxLength={120} style={field} value={draft.description}
          onChange={(e) => setDraft({ ...draft, description: e.target.value })} /></label>
        <div style={{ fontSize: 13, marginBottom: 6 }}>Starter questions (up to 4; otherwise use the site's questions)</div>
        {draft.starter_questions.map((q, i) => <div key={i} style={{ display: 'flex', gap: 8 }}>
          <input aria-label={`Audience starter question ${i + 1}`} value={q} maxLength={120} style={field}
            onChange={(e) => setDraft({ ...draft, starter_questions: draft.starter_questions.map((s, j) => j === i ? e.target.value : s) })} />
          <button type="button" style={button} aria-label={`Remove audience starter question ${i + 1}`}
            onClick={() => setDraft({ ...draft, starter_questions: draft.starter_questions.filter((_, j) => i !== j) })}>Remove</button>
        </div>)}
        {draft.starter_questions.length < 4 && <button type="button" style={button}
          onClick={() => setDraft({ ...draft, starter_questions: [...draft.starter_questions, ''] })}>Add starter question</button>}
        <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
          <button type="submit" style={button} disabled={saving || disabled}>{saving ? 'Saving…' : 'Save audience'}</button>
          <button type="button" style={button} disabled={saving} onClick={() => setDraft(null)}>Cancel</button>
        </div>
      </form>}
      {error && <p role="alert" style={{ color: '#b91c1c' }}>{error}</p>}
    </section>
  );
}

export function AudienceOptions({ audiences, value, onChange, disabled = false }) {
  return audiences.map((a) => <label key={a.id} style={{ display: 'block', padding: '5px 0', fontSize: 13 }}>
    <input type="checkbox" checked={value.includes(a.id)} disabled={disabled}
      onChange={(e) => onChange(e.target.checked ? [...value, a.id] : value.filter((id) => id !== a.id))} />{' '}{a.label}
  </label>);
}

export function AudiencePicker({ doc, audiences, onSave, disabled }) {
  const [open, setOpen] = React.useState(false);
  const [value, setValue] = React.useState(doc.audiences || []);
  const container = React.useRef(null);
  const close = React.useCallback(() => {
    setOpen(false);
    const old = doc.audiences || [];
    if (old.length !== value.length || old.some((id) => !value.includes(id))) onSave(value);
  }, [doc.audiences, value, onSave]);
  React.useEffect(() => {
    if (!open) return undefined;
    const outside = (e) => { if (!container.current?.contains(e.target)) close(); };
    document.addEventListener('pointerdown', outside);
    return () => document.removeEventListener('pointerdown', outside);
  }, [open, close]);
  return <div ref={container} style={{ position: 'relative' }}
    onBlur={(e) => { if (open && e.relatedTarget && !e.currentTarget.contains(e.relatedTarget)) close(); }}
    onKeyDown={(e) => { if (open && e.key === 'Escape') { e.stopPropagation(); close(); } }}>
    <button type="button" aria-label={`Audiences for ${doc.title}`} aria-expanded={open} disabled={disabled || !audiences.length}
      style={{ ...button, fontSize: 12, color: (doc.audiences || []).length ? '#1e5eb8' : '#777' }}
      onClick={() => { if (open) close(); else { setValue(doc.audiences || []); setOpen(true); } }}>
      {(doc.audiences || []).length ? doc.audiences.map((id) => <span key={id} style={{ display: 'inline-block', padding: '2px 5px', margin: 2, borderRadius: 999, background: '#eff6ff' }}>{audiences.find((a) => a.id === id)?.label || id}</span>) : 'Everyone'}
    </button>
    {open && <div role="group" aria-label={`Choose audiences for ${doc.title}`}
      style={{ position: 'absolute', zIndex: 5, top: '100%', right: 0, minWidth: 210, padding: 12, background: '#fff', border: '1px solid #ddd', borderRadius: 8, boxShadow: '0 4px 16px #0002' }}>
      <AudienceOptions audiences={audiences} value={value} onChange={setValue} />
      <div style={{ color: '#777', fontSize: 12, margin: '5px 0 8px' }}>No selections means everyone.</div>
      <button type="button" style={button} onClick={close}>Done</button>
    </div>}
  </div>;
}

export async function bulkAudienceUpdates(docIds, audiences, mode, send, onProgress) {
  const results = [];
  for (let start = 0; start < docIds.length; start += 200) {
    const body = await send({ doc_ids: docIds.slice(start, start + 200), audiences, mode });
    results.push(...body.results);
    onProgress(body.results, results.length, docIds.length);
  }
  return results;
}
